import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'react-bootstrap';
import Draggable from 'react-draggable';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import moment from 'moment';
import _ from 'lodash';
import { css } from '@emotion/core';
import { ScaleLoader } from 'react-spinners';
import { Tile, DisplayLogTitle, Body, Modal, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalInput, ModalTextarea, InerScroll } from './style/tile.style';
import EventSystem from './EventSystem';
import RmToolTip from './rmToolTip';
import RmInput from './rmInput';
import RmSendButton from './rmSendButton';

const override = css`
    display: block;
    margin: 0 auto;
    border-color: red;
    `;

export default function DisplayLog({
    ticketID, moduleIdentifier, externalDataUrl, logData, colours,
    touchDevice, toggleLogging, closeLog, raiseTicket, takeScreenshot,
}) {
    const [containerWidth, setContainerWidth] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [showSupportInfo, setShowSupportInfo] = useState(false);
    const [supportContactInfo, setSupportContactInfo] = useState(() => {
        const saved = JSON.parse(localStorage.getItem('supportContactInfo_'));
        if (saved) {
            return {
                contactName: saved.contactName ?? '',
                email: saved.email ?? '',
                phone: saved.phone ?? '',
            };
        }
        return { contactName: '', email: '', phone: '' };
    });
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [supportsScreenShot, setSupportsScreenShot] = useState(true);
    const [ticketCreatedMessage, setTicketCreatedMessage] = useState('');
    const [ticketBeingCreated, setTicketBeingCreated] = useState(false);
    const [moduleListOpen, setModuleListOpen] = useState(false);
    const [currentLogEntryIndex, setCurrentLogEntryIndex] = useState(-2);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [dataUrl, setDataUrl] = useState('');
    const [ticketDirect, setTicketDirect] = useState(false);
    const [canvasShapes, setCanvasShapes] = useState([]);
    const [imageAttached, setImageAttached] = useState(false);
    const [clickToDelete, setClickToDelete] = useState(-1);
    const [errorMessage, setErrorMessage] = useState(null);
    const [stopScrollingImage, setStopScrollingImage] = useState(false);
    const [autoFocusOnContact, setAutoFocusOnContact] = useState(false);
    const [textAreaHasFocus, setTextAreaHasFocus] = useState(false);

    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const drawCanvasRef = useRef(null);
    const saveCanvasRef = useRef(null);
    const imageRef = useRef(null);
    const mergedImageRef = useRef(null);
    const descriptionRef = useRef(null);
    const innerScrollRef = useRef(null);

    const drawingRectangleRef = useRef(false);
    const currentRectangleRef = useRef(null);

    useEffect(() => {
        setContainerWidth(containerRef.current.offsetWidth);
        if (typeof navigator.mediaDevices === 'undefined' || typeof navigator.mediaDevices.getDisplayMedia === 'undefined') {
            setSupportsScreenShot(false);
        }
    }, []);

    // CWRP ticketID
    const firstTicketRender = useRef(true);
    useEffect(() => {
        if (firstTicketRender.current) { firstTicketRender.current = false; return; }
        if (ticketID === 0) {
            setTicketCreatedMessage('');
        } else {
            setTicketCreatedMessage('Ticket created with ID: ' + ticketID);
            setImageAttached(false);
            setCanvasShapes([]);
        }
    }, [ticketID]);

    // CWRP moduleIdentifier
    const firstModuleIdRender = useRef(true);
    const prevModuleIdRef = useRef({ id0: null, id1: null });
    useEffect(() => {
        if (firstModuleIdRender.current) {
            firstModuleIdRender.current = false;
            prevModuleIdRef.current = { id0: moduleIdentifier[0], id1: moduleIdentifier[1] };
            return;
        }
        const prev = prevModuleIdRef.current;
        prevModuleIdRef.current = { id0: moduleIdentifier[0], id1: moduleIdentifier[1] };
        if (moduleIdentifier[1] !== prev.id1 && moduleIdentifier.length === 3) {
            const index = findLogEntryIndex(moduleIdentifier[1]);
            setCurrentLogEntryIndex(index);
            setTicketDirect(false);
        } else if (moduleIdentifier[0] !== prev.id0 && moduleIdentifier.length === 1) {
            setTicketDirect(true);
            openSupport();
        }
    }, [moduleIdentifier]);

    // CWRP externalDataUrl
    const firstDataUrlRender = useRef(true);
    useEffect(() => {
        if (firstDataUrlRender.current) { firstDataUrlRender.current = false; return; }
        if (externalDataUrl !== '') {
            imageRef.current.src = externalDataUrl;
            setShowSupportInfo(true);
        }
    }, [externalDataUrl]);

    function handleChange(e) {
        if (e.target.name === 'toggle') {
            toggleLogging(currentLogEntryIndex);
        } else if (e.target.name === 'contactName') {
            const updated = { ...supportContactInfo, contactName: e.target.value };
            setSupportContactInfo(updated);
            localStorage.setItem('supportContactInfo_', JSON.stringify(updated));
        } else if (e.target.name === 'email') {
            const updated = { ...supportContactInfo, email: e.target.value };
            setSupportContactInfo(updated);
            localStorage.setItem('supportContactInfo_', JSON.stringify(updated));
        } else if (e.target.name === 'phone') {
            const updated = { ...supportContactInfo, phone: e.target.value };
            setSupportContactInfo(updated);
            localStorage.setItem('supportContactInfo_', JSON.stringify(updated));
        } else if (e.target.name === 'subject') {
            setSubject(e.target.value);
        } else if (e.target.name === 'description') {
            setDescription(e.target.value);
        }
    }

    function closeSupport() {
        setShowSupportInfo(false);
        setAutoFocusOnContact(false);
        drawCanvasRef.current.removeEventListener('touchmove', drawCanvasShapes);
        drawCanvasRef.current.removeEventListener('touchstart', drawCanvasShapes);
        drawCanvasRef.current.removeEventListener('touchend', drawCanvasShapes);
        drawCanvasRef.current.removeEventListener('touchcancel', drawCanvasShapes);

        if (ticketBeingCreated) {
            setDescription('');
            setSubject('');
            setDataUrl('');
            setTicketBeingCreated(false);
            if (ticketDirect) {
                closeLog();
            } else {
                closeDisplayLog();
            }
        }

        if (ticketDirect) {
            closeLog();
        }
    }

    function openSupport() {
        setTicketCreatedMessage('');
        setShowSupportInfo(true);
        setAutoFocusOnContact(true);
        drawCanvasRef.current.addEventListener('touchmove', drawCanvasShapes);
        drawCanvasRef.current.addEventListener('touchstart', drawCanvasShapes);
        drawCanvasRef.current.addEventListener('touchend', drawCanvasShapes);
        drawCanvasRef.current.addEventListener('touchcancel', drawCanvasShapes);
    }

    function sendTicket() {
        const canvasCTX = canvasRef.current.getContext('2d');
        canvasCTX.drawImage(saveCanvasRef.current, 0, 0, saveCanvasRef.current.width, saveCanvasRef.current.height);
        const url = canvasRef.current.toDataURL();

        raiseTicket({
            contactName: supportContactInfo.contactName,
            email: supportContactInfo.email,
            phone: supportContactInfo.phone,
            subject,
            description,
            base64ImageContent: url.replace(/^data:image\/(png|jpg);base64,/, ''),
        });
        setTicketBeingCreated(true);
    }

    function createOptionItem(row) {
        const optionText = <span>{row.moduleName}<br /><span style={{ color: colours.color + '66' }}>{row.instanceName}</span></span>;
        return (
            <li style={{ paddingLeft: '5px', paddingTop: '3px', paddingBottom: '3px' }}
                className="module-list"
                key={'key' + row.elementID}
                data-id={row.elementID}>
                {optionText}
            </li>
        );
    }

    function moduleSelected(e) {
        let count = 0;
        let element = e.target;
        while (element.tagName !== 'LI') {
            element = element.parentElement;
            count++;
            if (count > 6) break;
        }
        setModuleListOpen(false);
        setCurrentLogEntryIndex(findLogEntryIndex(element.dataset.id));
    }

    function findLogEntryIndex(elementID) {
        let index = -1;
        if (logData.length > 0) {
            index = logData.findIndex(row => row.elementID === elementID);
        }
        if (currentLogEntryIndex >= 0) {
            EventSystem.publish('highlightModule' + logData[currentLogEntryIndex].elementID, false);
        }
        EventSystem.publish('highlightModule' + elementID, true);
        return index;
    }

    function closeDisplayLog() {
        setCurrentLogEntryIndex(-2);
        EventSystem.publish('highlightModule' + logData[currentLogEntryIndex].elementID, false);
        closeLog();
    }

    function fileSelected(e) {
        if (e.target.files[0].size > 2097152) {
            setErrorMessage('Image files must be less than 2MB in size');
            return;
        }
        setErrorMessage(null);
        const file = e.target.files[0];
        const reader = new FileReader();
        reader.onload = (event) => {
            imageRef.current.src = event.target.result;
            setShowFileUpload(false);
        };
        if (file) reader.readAsDataURL(file);
    }

    function captureScreen() {
        setShowSupportInfo(false);
        takeScreenshot();
    }

    function convertTempImageToCanvas() {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let scaleFactor = descriptionRef.current.offsetWidth / imageRef.current.width;
        if (descriptionRef.current.offsetWidth > imageRef.current.width) scaleFactor = 1;
        const canvasWidth = imageRef.current.width * scaleFactor;
        const canvasHeight = imageRef.current.height * scaleFactor;
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        drawCanvasRef.current.width = canvasWidth;
        drawCanvasRef.current.height = canvasHeight;
        saveCanvasRef.current.width = canvasWidth;
        saveCanvasRef.current.height = canvasHeight;
        ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
        setShowFileUpload(false);
        setImageAttached(true);
        setCanvasShapes([]);
    }

    function pasteReceived(e) {
        if (e.clipboardData.items[0].type.indexOf('image') === 0) {
            const imageBlob = e.clipboardData.items[0].getAsFile();
            if (imageBlob) {
                const URLObj = window.URL || window.webkitURL;
                imageRef.current.src = URLObj.createObjectURL(imageBlob);
            }
        }
    }

    function xlate(x, y) {
        const client = drawCanvasRef.current.getBoundingClientRect();
        const xCoord = x - client.x;
        const yCoord = y - client.y;
        return { x: xCoord < 0 ? 0 : xCoord, y: yCoord < 0 ? 0 : yCoord };
    }

    function adjustShapeTopLHC(canvasShape) {
        let topX, topY;
        if (canvasShape.width > 0 && canvasShape.height > 0) {
            topX = canvasShape.startX; topY = canvasShape.startY;
        } else if (canvasShape.width < 0 && canvasShape.height > 0) {
            topX = canvasShape.startX + canvasShape.width; topY = canvasShape.startY;
        } else if (canvasShape.width < 0 && canvasShape.height < 0) {
            topX = canvasShape.startX + canvasShape.width; topY = canvasShape.startY + canvasShape.height;
        } else {
            topX = canvasShape.startX; topY = canvasShape.startY + canvasShape.height;
        }
        return { shapeNumber: canvasShape.shapeNumber, startX: topX, startY: topY, width: Math.abs(canvasShape.width), height: Math.abs(canvasShape.height) };
    }

    function redrawSavedShapes(shapes, highlightIndex) {
        saveCanvasRef.current.getContext('2d').clearRect(0, 0, saveCanvasRef.current.width, saveCanvasRef.current.height);
        shapes.forEach((shape, i) => drawSavedShape(shape, i === highlightIndex ? 'yellow' : undefined));
    }

    function drawSavedShape(canvasShape, colour) {
        const saveCTX = saveCanvasRef.current.getContext('2d');
        const col = colour ?? 'red';
        saveCTX.lineWidth = 1;
        saveCTX.strokeStyle = col;
        saveCTX.setLineDash([]);
        saveCTX.strokeRect(canvasShape.startX, canvasShape.startY, canvasShape.width, canvasShape.height);
        saveCTX.fillStyle = col;
        saveCTX.fillRect(canvasShape.startX, canvasShape.startY, 20, 20);
        saveCTX.font = '14px sans-serif';
        saveCTX.fillStyle = 'black';
        saveCTX.textAlign = 'center';
        saveCTX.textBaseline = 'middle';
        saveCTX.fillText(canvasShape.shapeNumber, canvasShape.startX + 10, canvasShape.startY + 10);
    }

    function shapeClickedIn(x, y, shapes) {
        return shapes.findIndex(s => x >= s.startX && x <= s.startX + s.width && y >= s.startY && y <= s.startY + s.height);
    }

    function deleteSavedShape(index, shapes) {
        const newShapes = shapes.filter((_, i) => i !== index).map((s, i) => ({ ...s, shapeNumber: i + 1 }));
        setCanvasShapes(newShapes);
        redrawSavedShapes(newShapes, -1);
    }

    function drawCanvasShapes(e) {
        if (touchDevice && stopScrollingImage) e.preventDefault();
        if (touchDevice && (e.type === 'mousedown' || e.type === 'mouseup' || e.type === 'mousemove')) return;
        if (ticketBeingCreated) return;

        const drawCTX = drawCanvasRef.current.getContext('2d');

        if (e.type === 'mousedown' || e.type === 'touchstart') {
            drawingRectangleRef.current = true;
            const clientX = e.type === 'mousedown' ? e.clientX : e.changedTouches[0].clientX;
            const clientY = e.type === 'mousedown' ? e.clientY : e.changedTouches[0].clientY;
            const rp = xlate(clientX, clientY);
            currentRectangleRef.current = { startX: rp.x, startY: rp.y, height: 0, width: 0, startClientX: clientX, startClientY: clientY };

        } else if (e.type === 'mouseup' || e.type === 'touchend') {
            drawingRectangleRef.current = false;
            const clientX = e.type === 'mouseup' ? e.clientX : e.changedTouches[0].clientX;
            const clientY = e.type === 'mouseup' ? e.clientY : e.changedTouches[0].clientY;

            if (!stopScrollingImage && touchDevice) {
                if (Math.abs(currentRectangleRef.current.startClientX - clientX) > 10 ||
                    Math.abs(currentRectangleRef.current.startClientY - clientY) > 10) {
                    drawCTX.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
                    return;
                }
            }

            const rp = xlate(clientX, clientY);
            const height = rp.y - currentRectangleRef.current.startY;
            const width = rp.x - currentRectangleRef.current.startX;

            if (Math.abs(height) < 40 || Math.abs(width) < 40) {
                const clicked = shapeClickedIn(currentRectangleRef.current.startX, currentRectangleRef.current.startY, canvasShapes);
                if (clickToDelete > -1) {
                    if (clickToDelete === clicked) {
                        setClickToDelete(-1);
                        setErrorMessage(null);
                        deleteSavedShape(clicked, canvasShapes);
                    } else {
                        setClickToDelete(-1);
                        setErrorMessage(null);
                        redrawSavedShapes(canvasShapes, -1);
                    }
                } else {
                    if (clicked > -1) {
                        setClickToDelete(clicked);
                        setErrorMessage('Click shape again to delete');
                        redrawSavedShapes(canvasShapes, clicked);
                    } else if (touchDevice) {
                        setStopScrollingImage(prev => !prev);
                        drawCTX.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
                    }
                }
            } else {
                if (clickToDelete === -1) {
                    if (touchDevice && !stopScrollingImage) {
                        drawCTX.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
                        return;
                    }
                    let canvasShape = {
                        shapeNumber: canvasShapes.length + 1,
                        startX: currentRectangleRef.current.startX,
                        startY: currentRectangleRef.current.startY,
                        width,
                        height,
                    };
                    canvasShape = adjustShapeTopLHC(canvasShape);
                    const newShapes = [...canvasShapes, canvasShape];
                    setCanvasShapes(newShapes);
                    drawCTX.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
                    drawSavedShape(canvasShape);
                    setStopScrollingImage(false);
                }
            }

        } else if (e.type === 'mousemove' || e.type === 'touchmove') {
            if (clickToDelete === -1 && drawingRectangleRef.current) {
                drawCTX.clearRect(0, 0, drawCanvasRef.current.width, drawCanvasRef.current.height);
                drawCTX.lineWidth = 1;
                drawCTX.strokeStyle = 'red';
                drawCTX.setLineDash([5, 5]);
                const clientX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
                const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
                const rp = xlate(clientX, clientY);
                const height = rp.y - currentRectangleRef.current.startY;
                const width = rp.x - currentRectangleRef.current.startX;
                drawCTX.globalAlpha = 1;
                drawCTX.strokeRect(currentRectangleRef.current.startX, currentRectangleRef.current.startY, width, height);
                currentRectangleRef.current.height = height;
                currentRectangleRef.current.width = width;
            }
        }
    }

    const options = { defaultSortName: 'logDate', defaultSortOrder: 'desc' };

    let loggingStyle = {};
    let heading = '';
    let logHistory = [];
    let startLogging = false;
    const index = currentLogEntryIndex;
    if (index !== -2) {
        if (index === -1) {
            logHistory = [{ row: 0, message: 'Error - cannot find log for ' + moduleIdentifier[1] }];
        } else {
            logHistory = logData[index].logHistory;
            startLogging = logData[index].loggingEnabled;
            heading = (
                <div style={{ display: 'inline-block', whiteSpace: 'pre', lineHeight: logData[index].instanceName.length > 0 ? '14px' : '7px' }}>
                    {' '}{logData[index].moduleName}{' '}<br />
                    <span style={{ color: colours.color + '66', paddingLeft: '3px' }}>{logData[index].instanceName}</span>
                </div>
            );
            loggingStyle = { color: 'rgb(255,255,255,0.4)', alignSelf: 'center' };
        }
    }

    let currentOption = 0;
    if (typeof moduleIdentifier[1] !== 'undefined') currentOption = moduleIdentifier[1];

    const dropList = logData.map(row => createOptionItem(row));

    const placeHolderText = touchDevice
        ? 'Detailed description. To highlight specific items on the image, tap on it. The outside edge will turn red. Drag your finger over the area to draw a square. Tap twice in a square to remove it.'
        : 'Detailed description. \nDraw squares over the image to highlight specific items.\nClick and drag anywhere on the image to do this.\nClick twice in a square to remove it';

    const draggableContainerStyle = { height: '100vh', width: '100vw' };
    const errorBgColor = errorMessage === null ? 'rgb(0,0,0,0)' : 'red';

    return (
        <Tile className="dragable-container" style={draggableContainerStyle} ref={containerRef}>
            <Draggable handle=".logWindow" onStart={() => moduleListOpen ? false : setDragging(true)} onStop={() => setDragging(false)}>
                <div style={{ height: '238px', width: '45vw', minWidth: '320px', backgroundColor: colours.backgroundColor, boxShadow: '0px 0px 40px -26px white' }}>
                    <DisplayLogTitle className="logWindow" style={{ width: '100%', cursor: 'move', justifyContent: 'space-between', paddingLeft: '5px', display: 'flex' }}>
                        <div style={{ display: 'inline-block', cursor: 'pointer' }} onClick={() => setModuleListOpen(prev => !prev)}>
                            <i style={{ fontSize: '28px' }} className="material-icons">list</i>
                            {heading}
                        </div>
                        <ul style={{
                            borderStyle: 'none', width: '40%', height: '160px', lineHeight: '16px', whiteSpace: 'pre',
                            border: '1px solid #ffffff66', paddingLeft: '0px', listStyle: 'none', cursor: 'default',
                            position: 'absolute', top: '38px', left: '30px',
                            display: moduleListOpen ? 'block' : 'none', overflowX: 'auto',
                        }}
                            onClick={moduleSelected}
                            size="30"
                            className="module-ul"
                            value={currentOption}>
                            {dropList}
                        </ul>
                        <span style={loggingStyle}>
                            Logging{' '}
                            <input name="toggle" checked={startLogging} onChange={handleChange} type="checkbox" value="" />
                        </span>
                        <RmToolTip itemText='contact_support' itemType="icon" placement="bottom"
                            onClick={openSupport}
                            style={{ fontSize: '28px', display: 'inline-block', alignSelf: 'center' }}
                            toolTipText={<span>Create a<br />support ticket</span>} />
                        <div onClick={closeDisplayLog} style={{ cursor: 'pointer', alignSelf: 'center' }}>
                            <i style={{ fontSize: '28px' }} className="material-icons">close</i>
                        </div>
                    </DisplayLogTitle>
                    <Body style={{ width: '100%', height: '200px' }}>
                        <BootstrapTable data={logHistory} trClassName='rmTrClass' hover height="200px"
                            tableHeaderClass="col-hidden" bordered={false} options={options}>
                            <TableHeaderColumn hidden isKey={true} dataField='row'>row</TableHeaderColumn>
                            <TableHeaderColumn width="20%" dataField='logDate' tdStyle={{ padding: '0px 5px' }} dataFormat={formatName}>Name</TableHeaderColumn>
                            <TableHeaderColumn width="80%" dataField='message' tdStyle={{ whiteSpace: 'normal', padding: '2px 5px' }} columnClassName="message">Message</TableHeaderColumn>
                        </BootstrapTable>
                    </Body>
                </div>
            </Draggable>

            <div>
                <Modal show={showSupportInfo} onPaste={pasteReceived}>
                    <ModalContent>
                        <ModalHeader fontSize={window.innerWidth < 576 ? '20px' : '24px'}>
                            Create A Support Ticket
                            <div onClick={closeSupport} style={{ position: 'absolute', right: 0, top: 0, cursor: 'pointer', padding: '5px' }}>
                                <span style={{ fontSize: '14px', lineHeight: '28px', verticalAlign: 'top', paddingRight: '5px' }}>Close</span>
                                <i style={{ fontSize: '28px' }} className="material-icons">close</i>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <InerScroll ref={innerScrollRef}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', paddingRight: '15px', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'inline-flex', alignItems: 'end', flexGrow: 1, width: '200px' }}>
                                        <RmToolTip itemText='*' itemType="text" placement="top" noClick
                                            style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'relative' }}
                                            toolTipText={<span>Required</span>} />
                                        <RmInput type="text" placeholder="Name - first & last" name="contactName"
                                            readOnly={ticketBeingCreated} autoFocus={autoFocusOnContact} disabled={false}
                                            style={{ flexGrow: 2, flexShrink: 1, display: 'inline-flex', alignItems: 'flex-end', width: '200px' }}
                                            value={supportContactInfo.contactName} onChange={handleChange} />
                                    </div>
                                    <div style={{ display: 'inline-flex', alignItems: 'end', flexGrow: 1, width: '300px' }}>
                                        <RmToolTip itemText='*' itemType="text" placement="left" noClick
                                            style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'relative' }}
                                            toolTipText={<span>Required</span>} />
                                        <RmInput type="email" placeholder="Email address" name="email"
                                            readOnly={ticketBeingCreated} autoFocus={false} disabled={false}
                                            style={{ flexGrow: 3, flexShrink: 2, display: 'inline-block' }}
                                            value={supportContactInfo.email} onChange={handleChange} />
                                    </div>
                                    <div style={{ display: 'inline-flex', alignItems: 'end', flexGrow: 1, width: '100px' }}>
                                        <RmToolTip itemText='*' itemType="text" placement="left" noClick
                                            style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'relative' }}
                                            toolTipText={<span>Required</span>} />
                                        <RmInput type="text" placeholder="Telephone number" name="phone"
                                            readOnly={ticketBeingCreated} autoFocus={false} disabled={false}
                                            style={{ flexGrow: 2, flexShrink: 1, display: 'inline-block' }}
                                            value={supportContactInfo.phone} onChange={handleChange} />
                                    </div>
                                </div>

                                <div style={{ position: 'relative' }}>
                                    <RmToolTip itemText='*' itemType="text" placement="right" noClick
                                        style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'absolute' }}
                                        toolTipText={<span>Required</span>} />
                                    <div style={{ display: 'flex', flexWrap: 'wrap', paddingRight: '15px', color: '#333333' }}>
                                        <RmInput type="text" placeholder="Brief summary of issue" name="subject"
                                            readOnly={ticketBeingCreated} autoFocus={false} disabled={false}
                                            style={{ display: 'inline-flex', marginLeft: '10px', alignItems: 'flex-end', flexGrow: 1, flexShrink: 1, width: '200px' }}
                                            value={subject} onChange={handleChange} />
                                    </div>

                                    <RmToolTip itemText='*' itemType="text" placement="right" noClick
                                        style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'absolute' }}
                                        toolTipText={<span>Required</span>} />
                                    <div style={{ display: 'flex', flexWrap: 'wrap', paddingRight: '15px', color: '#333333' }}>
                                        <ModalTextarea type="text"
                                            style={{
                                                resize: 'none', width: '120px', marginLeft: '10px', flexGrow: 1, flexShrink: 1,
                                                marginTop: '15px', lineHeight: '18px', height: '120px',
                                                display: 'inline-flex', alignItems: 'flex-end',
                                                border: textAreaHasFocus ? '1px solid rgba(247, 191, 61, 0.4)' : '1px solid #c3c3c366',
                                            }}
                                            name="description"
                                            ref={descriptionRef}
                                            readOnly={ticketBeingCreated}
                                            value={description}
                                            onChange={handleChange}
                                            onFocus={() => setTextAreaHasFocus(true)}
                                            onBlur={() => setTextAreaHasFocus(false)}
                                            placeholder={placeHolderText} />
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: '10px', paddingRight: '15px', marginTop: '15px', marginBottom: '15px', lineHeight: '33px', height: '33px', maxWidth: '400px', flexWrap: 'wrap' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'end', flexGrow: 0 }}>
                                            <RmToolTip itemText='*' itemType="text" placement="right" noClick
                                                style={{ fontSize: '28px', lineHeight: '48px', display: 'inline-block', position: 'absolute', left: 0, alignSelf: 'flex-end' }}
                                                toolTipText={<span>Required</span>} />
                                            <span style={{ verticalAlign: 'text-bottom' }}>Screen Capture:</span>
                                        </div>
                                        <div style={{ display: 'inline-flex', alignItems: 'end', flexGrow: 0, justifyContent: 'flex-start', paddingLeft: '20px' }}>
                                            <RmToolTip itemText='attach_file' itemType="icon" placement="top"
                                                disabled={ticketBeingCreated}
                                                onClick={() => setShowFileUpload(prev => !prev)}
                                                style={{ fontSize: '28px', lineHeight: '14px', display: 'inline-block' }}
                                                toolTipText={<span>Attach file<br />containing the<br />screen image</span>} />
                                            <ModalInput type="file" id="screenCapture" name="imageFile"
                                                style={{ display: showFileUpload ? 'inline-flex' : 'none', paddingLeft: '10px', paddingTop: '5px', flexGrow: 0, width: '100%' }}
                                                accept=".jpg, .jpeg, .png"
                                                onChange={fileSelected} />
                                        </div>
                                        <RmToolTip itemText='screen_share' itemType="icon" placement="bottom"
                                            disabled={ticketBeingCreated}
                                            onClick={captureScreen}
                                            style={{ fontSize: '28px', lineHeight: '14px', alignItems: 'end', paddingLeft: '20px', display: !supportsScreenShot || showFileUpload ? 'none' : 'inline-flex', justifyContent: 'flex-start' }}
                                            toolTipText={<span>Capture screen image.<br /> Choose Orquestradio<br />from tab or window list.</span>} />
                                        <div style={{ display: touchDevice ? 'none' : 'inline-flex', alignItems: 'center', paddingLeft: '20px', flexGrow: 0, width: '80px', justifyContent: 'flex-start' }}>
                                            <RmToolTip itemText='or paste' itemType="text" placement="bottom"
                                                disabled={ticketBeingCreated} noClick
                                                style={{ fontSize: '14px', lineHeight: '14px', display: showFileUpload ? 'none' : 'inline-block' }}
                                                toolTipText={<span>Ctrl-V</span>} />
                                        </div>
                                    </div>
                                </div>
                                <br />

                                <div style={{ position: 'relative', display: imageAttached || ticketBeingCreated ? 'block' : 'none' }}>
                                    <canvas ref={canvasRef} style={{ marginLeft: '10px', position: 'relative', zIndex: 0, border: '1px dotted white', boxShadow: stopScrollingImage ? '1px -1px 8px 4px red' : 'none' }} />
                                    <canvas ref={saveCanvasRef} style={{ zIndex: 0, marginLeft: '10px', position: 'absolute', top: 0, left: 0 }} />
                                    <canvas ref={drawCanvasRef}
                                        style={{ zIndex: 1, marginLeft: '10px', position: 'absolute', top: 0, left: 0, touchAction: stopScrollingImage ? 'none' : 'auto' }}
                                        onMouseDown={drawCanvasShapes}
                                        onMouseUp={drawCanvasShapes}
                                        onMouseMove={drawCanvasShapes} />
                                </div>
                            </InerScroll>
                        </ModalBody>
                        <ModalFooter justifyContent={ticketBeingCreated ? 'space-between' : 'flex-end'}>
                            <div style={{ textAlign: 'center', color: 'white', backgroundColor: errorBgColor, marginRight: '15px', position: 'absolute', top: 0, left: 0, width: '100%' }}>
                                {errorMessage}
                            </div>
                            <div style={{ color: 'red', fontSize: '18px', paddingTop: '2px', paddingLeft: '10px' }}>
                                <ScaleLoader loading={ticketBeingCreated && ticketCreatedMessage.length === 0} css={css`height: 40px;`} color={'red'} />
                                {ticketCreatedMessage}
                            </div>
                            <div style={{ display: ticketBeingCreated === false ? 'inline-flex' : 'none', lineHeight: '24px' }}>
                                Create Ticket{' '}
                                <RmSendButton className="material-icons"
                                    style={{ display: 'inline-block', paddingRight: '10px' }}
                                    disabled={
                                        supportContactInfo.contactName.length === 0 ||
                                        supportContactInfo.email.length === 0 ||
                                        supportContactInfo.phone.length === 0 ||
                                        subject.length === 0 ||
                                        !imageAttached ||
                                        errorMessage !== null ||
                                        description.length === 0
                                    }
                                    fontSize="24px"
                                    onClick={sendTicket} />
                            </div>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </div>

            <img ref={imageRef} style={{ visibility: 'hidden' }} onLoad={convertTempImageToCanvas} />
            <img ref={mergedImageRef} style={{ visibility: 'hidden' }} onLoad={sendTicket} />
        </Tile>
    );
}

function formatName(cell) {
    return moment(cell).format('HH:mm:ss.SSS');
}
